import { Stack, Typography, Box, Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';

import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { QUERY_KEY } from '@/api/endpoints/conversation/useGetInboxConversations';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import EmailSendModal from '@/components/email/EmailSendModal';

import InboxSubpage from './inbox/InboxSubpage';

const InboxPage: React.FC = () => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { selectedPropertyLocation } = usePropertyManager();
  const queryClient = useQueryClient();

  const handleOpenEmailModal = () => {
    setShowEmailModal(true);
  };

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
  };

  const handleEmailSent = () => {
    // Auto-refresh the inbox conversations after email is sent
    if (selectedPropertyLocation) {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY({ location_id: selectedPropertyLocation.id }),
      });
    }
  };

  return (
    <MuiPageWrapper>
      <Stack
        direction="column"
        sx={{ flex: '1 1 100%', backgroundColor: '#ffffff', minHeight: '100vh' }}
      >
        {/* Gmail-style header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 400,
                color: '#202124',
                fontSize: '1.375rem',
                lineHeight: 1.3,
              }}
            >
              Inbox
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#5f6368',
                mt: 0.5,
                fontSize: '0.875rem',
              }}
            >
              View and manage all conversations with your contacts.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={handleOpenEmailModal}
            disabled={!selectedPropertyLocation}
            color="primary"
          >
            Compose Email
          </Button>
        </Box>

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: '#ffffff',
            overflow: 'hidden',
          }}
        >
          <Switch>
            <Route exact path={PMPRoutes.inbox} component={InboxSubpage} />
            <Redirect to={PMPRoutes.inbox} />
          </Switch>
        </Box>
      </Stack>

      {/* Email Send Modal */}
      {showEmailModal && selectedPropertyLocation && (
        <EmailSendModal
          locationId={selectedPropertyLocation.id}
          onClose={handleCloseEmailModal}
          onEmailSent={handleEmailSent}
        />
      )}
    </MuiPageWrapper>
  );
};

export default InboxPage;
